/**
 * Helpers type-safe pra extrair mensagem/código/detalhes de erros vindos da API.
 *
 * O interceptor em `services/api.ts:113-121` rejeita com um plain object
 * no formato `ApiError` (`{ statusCode, error, message, code, details }`) —
 * **não** com um `AxiosError`. Por isso `instanceof AxiosError` sozinho não
 * casa em 99% dos `.catch` do app, e helpers que dependiam só dele caíam
 * silenciosamente no fallback genérico. Aqui usamos um type guard duck-typed
 * pra reconhecer essa forma, e mantemos os branches de `AxiosError`/`Error`
 * como defensivos (caso a request falhe antes do interceptor processar).
 *
 * ```ts
 * } catch (error: unknown) {
 *   const code = getErrorCode(error)                 // 'OAUTH_USER_NO_PASSWORD_RESET' | undefined
 *   const msg = getErrorMessage(error, 'fallback')   // mensagem do back ou fallback
 *   const details = getErrorDetails(error)           // [{field, message}] | undefined (Zod)
 * }
 * ```
 */
import { AxiosError } from 'axios'
import type { ApiError } from '@/types'

interface ApiErrorBody {
  message?: string
  error?: string
  code?: string
  details?: ApiError['details']
}

/**
 * Reconhece o `ApiError` plain object devolvido pelo interceptor axios.
 * `instanceof` não funciona — o objeto não tem prototype de `Error`.
 * Checa `statusCode: number` + `message: string` pra evitar falso positivo
 * com qualquer `{}` aleatório.
 */
function isApiErrorLike(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    typeof (error as { statusCode: unknown }).statusCode === 'number' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  )
}

export function getErrorMessage(error: unknown, defaultMessage = 'Erro inesperado'): string {
  if (isApiErrorLike(error)) {
    return error.message || error.error || defaultMessage
  }
  if (error instanceof AxiosError) {
    const body = error.response?.data as ApiErrorBody | undefined
    return body?.message ?? body?.error ?? error.message ?? defaultMessage
  }
  if (error instanceof Error) {
    return error.message || defaultMessage
  }
  if (typeof error === 'string') {
    return error
  }
  return defaultMessage
}

export function getErrorCode(error: unknown): string | undefined {
  if (isApiErrorLike(error)) {
    return error.code
  }
  if (error instanceof AxiosError) {
    const body = error.response?.data as ApiErrorBody | undefined
    return body?.code
  }
  return undefined
}

export function getErrorDetails(error: unknown): ApiError['details'] {
  if (isApiErrorLike(error)) {
    return error.details
  }
  if (error instanceof AxiosError) {
    const body = error.response?.data as ApiErrorBody | undefined
    return body?.details
  }
  return undefined
}
